import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import { AxiosError } from "axios";
import { Flex, Button, Tooltip, Dropdown, TextArea, PopupProps, MenuButton, SplitButton } from "@fluentui/react-northstar";
import {
    SaveIcon, CheckmarkCircleIcon, OpenOutsideIcon, LinkIcon, ShiftActivityIcon, ComposeIcon, CloseIcon
} from "@fluentui/react-icons-northstar";
import DatepickerWithLabel from "../components/pickers/datepickerWithLabel";
import InputWithLabel from "../components/common/inputWithLabel";
import Alert from "../components/common/alert";

import { useAuth } from "../contexts/authContext";
import useClearAuthIssue from "../common/useClearAuthIssue";
import { useApp } from "../contexts/appContext";
import { completeTask, cancelTask, upsertTask } from "../services/dataService";
import { useInvalidateData, useTask } from "../queries";
import { parseDateToDate } from "../common/dateOperations";
import useSaveShortcut from "../common/useSaveShortcut";
import { copyToClipboard, hasValue, priorities, priorityCodeToText, priorityTextToCode } from "../common/textOperations";
import styles from "../common.module.scss";
import { getSettings } from "../services/settingsService";
import ITask from "../interfaces/ITask";
import ProjectPicker from "../components/pickers/projectPicker";
import FieldWithLabel from "../components/common/fieldWithLabel";
import UserPicker from "../components/pickers/userPicker";

const TaskForm: React.FunctionComponent = () => {
    document.title = "Task";
    const settings = getSettings();

    const { id } = useParams<{ id?: string }>();
    const [errorMessage, setErrorMessage] = useState("");
    const [authIssue, setAuthIssue] = useState(true);
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState<string | undefined>();
    const [dueDate, setDueDate] = useState(new Date());
    const [percentageCompleted, setPercentageCompleted] = useState("0");
    const [priority, setPriority] = useState(priorities[1].text);
    const [owner, setOwner] = useState<string | undefined>();
    const [regardingIsProject, setRegardingIsProject] = useState<boolean>(true);
    const [selectedProject, setSelectedProject] = useState("");
    const [showAddTooltip, setShowAddTooltip] = useState<boolean | undefined>();

    const { state: authState } = useAuth();
    const { accessToken: token } = authState;
    useClearAuthIssue(authIssue, () => { setErrorMessage(""); setAuthIssue(false); });
    const history = useHistory();
    const { state: appState, dispatch } = useApp();

    const { data: task } = useTask(id);
    const { invalidateTasks } = useInvalidateData();

    const priorityItems = priorities.map(x => x.text);
    const readOnly: boolean = !!task && !(task.id && (task.myRecord || task.iAmAuthor));
    const active: boolean = task?.active ?? true;

    // The task may arrive after the first render, but the form must be filled only once
    // so that a background refresh never throws away what the user is typing
    const filledFromTask = useRef(false);

    useEffect(() => {
        if (!task || filledFromTask.current) { return; }
        filledFromTask.current = true;

        setSubject(task.subject);
        setDescription(task.description);
        setDueDate(task.dueDate ? new Date(task.dueDate) : new Date());
        setPercentageCompleted(task.completedPercentage?.toString() ?? "0");
        setPriority(task ? priorityCodeToText(task.priority) : priorities[1].text);
        setOwner(task?.owner?.id);
        const isProject = task?.regarding?.typeCode === "incident" || !task;
        setRegardingIsProject(isProject);
        setSelectedProject(isProject ? task?.regarding?.id ?? "" : "");
    }, [task]);

    const toggleLoading = (loading: boolean) => {
        dispatch({ type: "SET_LOADER", show: loading });
    };

    const asyncCall = async (callback: any, navigateToList: boolean = true) => {
        setErrorMessage("");
        setAuthIssue(false);

        toggleLoading(true);
        try {
            await callback;
            toggleLoading(false);
            if (navigateToList) {
                invalidateTasks();
                history.push("/tasks");
            }
        } catch (error) {
            const err: AxiosError = error as AxiosError;
            toggleLoading(false);
            if (err.response?.status === 401) {
                setErrorMessage("Přístupový token vypršel.");
                setAuthIssue(true);
            } else {
                setErrorMessage(err.message);
                console.log(err.response?.data);
            }
        }
    }

    const upsert = async () => {
        if (!token) { return; }

        const data: ITask = {
            id,
            completedPercentage: parseInt(percentageCompleted, 0),
            priority: priorityTextToCode(priority),
            subject,
            description,
            dueDate,
            owner: owner ? {
                viewOnlyOwnReports: false,
                id: owner
            } : undefined,
            regarding: regardingIsProject && selectedProject ? { id: selectedProject } : undefined
        };
        await asyncCall(upsertTask(token, data));
    };

    const complete = async () => {
        if (!token || !id) { return; }
        await asyncCall(completeTask(token, id));
    }

    const cancel = async () => {
        if (!token || !id) { return; }
        await asyncCall(cancelTask(token, id));
    }

    const itemLink = () => {
        return import.meta.env.VITE_CRM_BASE_URL + `&pagetype=entityrecord&etn=task&id=${id}`;
    };

    const openOrCopyLink = (link?: string) => {
        if (settings.copyLinks) {
            copyToClipboard(link ?? "");
        } else {
            window.open(link);
        }
    };

    const modalResultClose = (_: any) => {
        invalidateTasks();
        history.push("/tasks");
    };

    const showModalClose = () => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Potvrzení",
            size: {
                height: 310,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/project/confirmtask?id=${id}&projectId=${selectedProject}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultClose);
    };

    const completeButtonClicked = async () => {
        if (regardingIsProject) {
            showModalClose();
        } else {
            await complete();
        }
    }

    const redirectToNewReport = () => {
        history.push(`/report/new?initProjectId=${selectedProject}`);
    }

    const formIsValid = () => {
        let isValid: boolean = true;
        if (regardingIsProject) {
            isValid = hasValue(selectedProject);
        }

        return (
            hasValue(subject) &&
            hasValue(dueDate) &&
            isValid
        );
    };

    useSaveShortcut(upsert, active && !readOnly && formIsValid() && !appState.showLoader);

    return (
        <div>
            <Flex column gap="gap.medium" className={styles.formBody}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 15 }}>
                    <div className={styles.flex1}>
                        <Flex column gap="gap.smaller">
                            <small>Priorita</small>
                            <Dropdown
                                items={priorityItems}
                                fluid
                                value={priority}
                                onChange={(_: any, data: any) => setPriority(data.value)}
                            />
                        </Flex>
                    </div>

                    {task?.id &&
                        <div className={styles.flex1}>
                            <InputWithLabel
                                label="Hotovo&nbsp;%"
                                value={percentageCompleted.toString()}
                                onChange={(_: any, data: any) => setPercentageCompleted(data.value)}
                            />
                        </div>
                    }

                    <div className={styles.flex05}>
                        <DatepickerWithLabel
                            label="Termín"
                            date={parseDateToDate(dueDate)}
                            hideTime
                            onChange={(date: Date) => setDueDate(date)}
                        />
                    </div>

                    <div className={styles.flex2} style={{ minWidth: 200 }}>
                        <Flex column gap="gap.smaller">
                            <small>Vlastník</small>
                            <UserPicker selectedUserId={owner} userSelected={(u) => setOwner(u?.id)} />
                        </Flex>
                    </div>
                </div>

                <InputWithLabel label="Předmět" value={subject} onChange={(_: any, data: any) => setSubject(data.value)} maxLength={328} />
                {regardingIsProject ?
                    <Flex column gap="gap.smaller">
                        <small>Případ</small>
                        <ProjectPicker selectedProjectId={selectedProject} projectSelected={(x) => setSelectedProject(x)} viewOnlyOwn={false} />
                    </Flex>
                    :
                    <>
                        {task?.regarding?.id &&
                            <FieldWithLabel label={task.regarding.typeName} value={task.regarding.title} />
                        }
                    </>
                }
                <Flex column gap="gap.smaller">
                    <small>Popis</small>
                    <TextArea
                        value={description}
                        fluid
                        onChange={(_: any, data: any) => setDescription(data.value)}
                        maxLength={2000}
                        rows={13}
                        resize={"vertical"}
                    />
                </Flex>
                {task?.link &&
                    <Flex>
                        {settings.copyLinks ?
                            <Button primary text content="Kopírovat odkaz z popisu" icon={<LinkIcon />} onClick={() => openOrCopyLink(task.link)} />
                            :
                            <Button primary text content="Otevřít odkaz z popisu" icon={<OpenOutsideIcon />} onClick={() => openOrCopyLink(task.link)} />
                        }
                    </Flex>
                }
                {task?.author &&
                    <FieldWithLabel label="Zadavatel" value={task?.author?.displayName} />
                }
                {errorMessage && <Alert type="error" authIssue={authIssue}>{errorMessage}</Alert>}
            </Flex>

            <div className={styles.footer} style={{ background: appState.backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    {task?.id &&
                        <>
                            {settings.copyLinks ?
                                <Tooltip
                                    trigger={<Button text iconOnly icon={<LinkIcon />} onClick={() => openOrCopyLink(itemLink())} />}
                                    content="Kopírovat odkaz do CRM"
                                />
                                :
                                <Tooltip
                                    trigger={<Button text iconOnly icon={<OpenOutsideIcon />} onClick={() => openOrCopyLink(itemLink())} />}
                                    content="Otevřít úkol v CRM"
                                />
                            }
                            {regardingIsProject &&
                                <Tooltip
                                    content="Přidat..."
                                    open={showAddTooltip}
                                    trigger={
                                        <MenuButton
                                            onOpenChange={(_: any, data: PopupProps | undefined) => setShowAddTooltip(data?.open ? false : undefined)}
                                            trigger={<Button text iconOnly icon={<ComposeIcon />} />}
                                            menu={[
                                                {
                                                    key: "add-report",
                                                    icon: <ShiftActivityIcon outline />,
                                                    content: "Přidat výkaz",
                                                    onClick: redirectToNewReport
                                                }
                                            ]}
                                        />
                                    }
                                />
                            }
                        </>
                    }
                    <Flex.Item push><div></div></Flex.Item> {/* Just to move it to the right  */}

                    {active && !readOnly &&
                        <SplitButton
                            menu={[
                                {
                                    key: "save",
                                    content: "Uložit",
                                    icon: <SaveIcon outline />,
                                    onClick: upsert,
                                    disabled: !formIsValid()
                                },
                                {
                                    key: "complete",
                                    content: "Hotovo",
                                    icon: <CheckmarkCircleIcon outline />,
                                    onClick: completeButtonClicked,
                                    disabled: !(!!task?.id && task?.myRecord)
                                },
                                {
                                    key: "cancel",
                                    content: "Zrušit",
                                    icon: <CloseIcon outline />,
                                    onClick: cancel,
                                    disabled: !(!!task?.id && task?.myRecord)
                                }
                            ]}
                            button={{
                                content: "Uložit",
                                icon: <SaveIcon outline />,
                                loading: appState.showLoader,
                                onClick: upsert,
                                disabled: !formIsValid() || appState.showLoader
                            }}
                            primary
                            disabled={appState.showLoader}
                        />
                    }
                </Flex>
            </div>
        </div>
    );
};

export default TaskForm;
